import { useState, type SyntheticEvent } from "react";
import { useAction } from "convex/react";
import { ArrowLeft, Mail } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../ui/button";
import { errorMessage } from "../workspace/format";

export function EmailCodeVerification({ disabled = false }: { disabled?: boolean }) {
  const begin = useAction(api.emailVerification.begin);
  const finish = useAction(api.emailVerification.finish);
  const [request, setRequest] = useState<{ flowId: string; maskedEmail: string } | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await begin({});
      setRequest({ flowId: result.flowId, maskedEmail: result.maskedEmail });
    } catch (caught) {
      setError(errorMessage(caught) || "We couldn’t send the email code. Try another option.");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (request === null) return;
    setBusy(true);
    setError(null);
    try {
      await finish({ flowId: request.flowId, code });
    } catch (caught) {
      setError(errorMessage(caught) || "That email code is incorrect or expired.");
    } finally {
      setBusy(false);
    }
  };

  if (request === null) {
    return <>
      <Button className="auth-method" variant="secondary" disabled={disabled || busy} onClick={() => { void sendCode(); }}>
        <Mail size={18} /><span><strong>{busy ? "Sending email code…" : "Email me a one-time code"}</strong><small>Six digits · expires in 10 minutes</small></span>
      </Button>
      {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
    </>;
  }

  return <form className="email-code-form" onSubmit={(event) => { void verifyCode(event); }}>
    <p>Code sent to <strong>{request.maskedEmail}</strong></p>
    <label>Email code<input aria-label="Six-digit email code" value={code} onChange={(event) => { setCode(event.target.value.replace(/\D/gu, "").slice(0, 6)); }} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} placeholder="000000" required /></label>
    {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
    <Button type="submit" variant="primary" disabled={disabled || busy || code.length !== 6}>{busy ? "Checking code…" : "Verify email code"}</Button>
    <button type="button" className="link-button" disabled={busy} onClick={() => { setRequest(null); setCode(""); setError(null); }}><ArrowLeft size={13} /> Use another option</button>
  </form>;
}
